import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { ErrorBoundary } from '@/components/error-boundary/error-boundary';
import { apiClient } from '@/modules/api/api-client';
import { FREQUENT_STALE_TIME, queryKeys } from '@/modules/query';
import { formatRelativeTime } from '@/utils/format-time';
import { DASHBOARD_TITLE_MAX, truncateTitle } from '@/utils/truncate';

interface PrSummary {
  prId: string;
  title: string;
  authorId: string;
  authorDisplayName?: string;
  sourceBranch: string;
  destinationBranch: string;
  status: 'OPEN' | 'CLOSED' | 'MERGED';
  createdAt: string;
  lastActivityDate: string;
  repositoryName?: string;
  approvalCount?: number;
  ciStatus?: 'PENDING' | 'RUNNING' | 'PASSED' | 'FAILED';
}

interface DashboardPrResponse {
  needsReview: PrSummary[];
  yourPrs: PrSummary[];
}

interface NotificationCountResponse {
  unreadCount: number;
}

function CiStatusIndicator({ status }: { status?: string }) {
  if (!status) {
    return <span className="inline-block h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" aria-label="No CI status" />;
  }

  switch (status) {
    case 'PASSED':
      return <span className="inline-block h-3 w-3 rounded-full bg-success" aria-label="CI passed" />;
    case 'FAILED':
      return <span className="inline-block h-3 w-3 rounded-full bg-danger" aria-label="CI failed" />;
    case 'PENDING':
    case 'RUNNING':
      return <span className="inline-block h-3 w-3 rounded-full bg-warning" aria-label="CI pending" />;
    default:
      return <span className="inline-block h-3 w-3 rounded-full bg-gray-300 dark:bg-gray-600" aria-label="No CI status" />;
  }
}

function PrEntry({ pr, linkTo }: { pr: PrSummary; linkTo?: string }) {
  const content = (
    <>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-content">
          {truncateTitle(pr.title, DASHBOARD_TITLE_MAX)}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-content-tertiary">
          {pr.repositoryName && <span>{pr.repositoryName}</span>}
          <span>{pr.authorDisplayName ?? pr.authorId}</span>
          <span>{formatRelativeTime(pr.createdAt)}</span>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {pr.approvalCount !== undefined && pr.approvalCount > 0 && (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200" aria-label={`${pr.approvalCount} approval${pr.approvalCount !== 1 ? 's' : ''}`}>
            {pr.approvalCount}
          </span>
        )}
        <CiStatusIndicator status={pr.ciStatus} />
      </div>
    </>
  );

  if (linkTo) {
    return (
      <li>
        <Link
          to={linkTo}
          className="flex items-center justify-between gap-4 rounded-md border border-border p-4 hover:bg-surface-raised hover:border-brand-300 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2"
        >
          {content}
        </Link>
      </li>
    );
  }

  return (
    <li className="flex items-center justify-between gap-4 rounded-md border border-border p-4">
      {content}
    </li>
  );
}

function PrListSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading pull requests">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="animate-pulse rounded-md border border-border p-4"
        >
          <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="mt-2 h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      ))}
    </div>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="rounded-md border border-danger/30 bg-danger/10 p-4">
      <p className="text-sm text-danger">{message}</p>
      <button
        onClick={onRetry}
        className="mt-2 rounded-md bg-danger px-3 py-1 text-xs font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-2"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-border p-6 text-center">
      <p className="text-sm text-content-tertiary">{message}</p>
    </div>
  );
}

function NeedsYourReviewSection() {
  const { data, isLoading, isError, refetch } = useQuery<DashboardPrResponse>({
    queryKey: queryKeys.prs.list('dashboard', 'needs-review'),
    queryFn: () => apiClient.get<DashboardPrResponse>('/prs/dashboard'),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return <PrListSkeleton />;
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load pull requests needing your review."
        onRetry={() => refetch()}
      />
    );
  }

  const prs = data?.needsReview ?? [];

  if (prs.length === 0) {
    return <EmptyState message="No pull requests need your review right now." />;
  }

  return (
    <ul className="space-y-3" aria-label="Pull requests awaiting your review">
      {prs.map((pr) => (
        <PrEntry
          key={pr.prId}
          pr={pr}
          linkTo={pr.repositoryName ? `/repos/${pr.repositoryName}/prs/${pr.prId}` : undefined}
        />
      ))}
    </ul>
  );
}

function YourPullRequestsSection() {
  const { data, isLoading, isError, refetch } = useQuery<DashboardPrResponse>({
    queryKey: queryKeys.prs.list('dashboard', 'your-prs'),
    queryFn: () => apiClient.get<DashboardPrResponse>('/prs/dashboard'),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return <PrListSkeleton />;
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load your pull requests."
        onRetry={() => refetch()}
      />
    );
  }

  const prs = data?.yourPrs ?? [];

  if (prs.length === 0) {
    return <EmptyState message="You have no pull requests with recent activity." />;
  }

  return (
    <ul className="space-y-3" aria-label="Your pull requests">
      {prs.map((pr) => (
        <PrEntry key={pr.prId} pr={pr} />
      ))}
    </ul>
  );
}

function NotificationSummary() {
  const { data, isLoading, isError, refetch } = useQuery<NotificationCountResponse>({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: () => apiClient.get<NotificationCountResponse>('/notifications/unread-count'),
    staleTime: FREQUENT_STALE_TIME,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse" aria-busy="true" aria-label="Loading notifications">
        <div className="h-5 w-40 rounded bg-gray-200 dark:bg-gray-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <SectionError
        message="Failed to load notification count."
        onRetry={() => refetch()}
      />
    );
  }

  const count = data?.unreadCount ?? 0;

  return (
    <p className="text-sm text-content-secondary">
      {count === 0
        ? 'No unread notifications'
        : `${count} unread notification${count !== 1 ? 's' : ''}`}
    </p>
  );
}

export function DashboardPage() {
  return (
    <main id="main-content" className="mx-auto max-w-4xl px-4 py-6">
      <h1 className="text-2xl font-bold text-content">
        Dashboard
      </h1>

      <div className="mt-4">
        <ErrorBoundary
          fallback={
            <SectionError
              message="Failed to load notification summary."
              onRetry={() => window.location.reload()}
            />
          }
        >
          <NotificationSummary />
        </ErrorBoundary>
      </div>

      <div className="mt-8 space-y-8">
        <section>
          <h2 className="mb-4 text-lg font-semibold text-content">
            Awaiting Review
          </h2>
          <ErrorBoundary
            fallback={
              <SectionError
                message="An unexpected error occurred in this section."
                onRetry={() => window.location.reload()}
              />
            }
          >
            <NeedsYourReviewSection />
          </ErrorBoundary>
        </section>

        <section>
          <h2 className="mb-4 text-lg font-semibold text-content">
            My Pull Requests
          </h2>
          <ErrorBoundary
            fallback={
              <SectionError
                message="An unexpected error occurred in this section."
                onRetry={() => window.location.reload()}
              />
            }
          >
            <YourPullRequestsSection />
          </ErrorBoundary>
        </section>
      </div>
    </main>
  );
}
